<template>
  <div>
    <Head title="Projections" />
    <h1 class="mb-8 text-3xl font-bold">Projections</h1>
    <div class="flex items-center justify-between mb-6">
      <search-filter v-model="form.search" class="mr-4 w-full max-w-md" @reset="reset" />
      <a
        v-if="user.group_permissions.includes('add_projections')
          || user.per_user_permissions.includes('add_projections')"
        class="btn-orange" href="#" @click="openUploadWindow"
      >
        <span>Upload</span>
        <span class="hidden md:inline">&nbsp;Projections</span>
      </a>
    </div>
    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <th class="pb-3 pt-3 px-3">Upload date</th>
          <th class="pb-3 pt-3 px-3">Financial Year</th>
          <th class="pb-3 pt-3 px-3">Categories</th>
          <th class="pb-3 pt-3 px-3">Uploaded by</th>
          <th class="pb-3 pt-3 px-3">Uploaded on</th>
          <th class="pb-3 pt-3 px-3" />
        </tr>
        <tr v-for="projection in projections.data" :key="projection.id" class="hover:bg-orange-100 focus-within:bg-gray-100">
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/projection/${projection.id}/projection-entries`">
              {{ projection.date }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/projection/${projection.id}/projection-entries`">
              {{ projection.financial_year }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/projection/${projection.id}/projection-entries`">
              {{ projection.total_categories }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/projection/${projection.id}/projection-entries`">
              {{ projection.user }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/projection/${projection.id}/projection-entries`">
              {{ projection.uploaded_on }}
            </Link>
          </td>
          <td class="w-px border-t">
            <Link class="flex items-center px-3" :href="`/projection/${projection.id}/projection-entries`" tabindex="-1">
              <icon name="cheveron-right" class="block w-6 h-6 fill-gray-400" />
            </Link>
          </td>
        </tr>
        <tr v-if="projections.data.length === 0">
          <td class="px-3 py-3 border-t" colspan="6">No data found</td>
        </tr>
      </table>
    </div>
    <pagination class="mt-6" :links="projections.links" />
  </div>
  <div v-if="openUpload" class="fixed z-10 inset-0 overflow-y-auto">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75" />
      </div>
      <!-- This element is to trick the browser into centering the modal contents. -->
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" />
      <div class="inline-block align-bottom bg-white w-1/2 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
        <form>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex modal-wrapper">
            <h1 style="font-size: 20px;">Upload Projections File</h1>
          </div>
          <div class="bg-white px-4 pt-0 pb-4 sm:p-6 sm:pb-4">
            <div class="mb-4">
              <form enctype="multipart/form-data" @submit.prevent="store">
                <div class="flex flex-wrap -mb-2 -mr-6 pl-8 pr-8 pt-8 pb-2">
                  <file-input v-model="formData.projection_file" :error="$props.errors.projection_file" class="pb-2 pr-6 w-full lg:w-full" type="file" accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" label="Projections Excel File" />
                  <p v-if="$props.errors.projection_file"><small class="text-red-700">{{ $props.errors.projection_file }}</small></p>
                </div>
                <div class="flex flex-wrap -mb-8 -mr-6 pt-2 pl-8 pr-8 pb-8">
                  <select-input v-model="formData.financial_year" :error="$props.errors.financial_year" class="pb-8 pr-6 w-full lg:w-full" label="Financial Year">
                    <option v-for="year in years" :key="year.id">{{ year.name }}</option>
                  </select-input>
                </div>
                <div class="flex flex-wrap -mb-8 -mr-6 pt-2 pl-8 pr-8 pb-8">
                  <Checkbox label="Overwrite Existing Categories" :active="active" @input="overwrite = $event" />
                </div>
              </form>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-green-500 focus:outline-none focus:border-green-700 focus:shadow-outline-red sm:text-sm sm:leading-5" @click="store">
                Process
              </button>
            </span>
            <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5" @click="closeModal">
                Close
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
    
<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import SearchFilter from '@/Shared/SearchFilter'
import Pagination from '@/Shared/Pagination'
import FileInput from '@/Shared/FileInput'
import SelectInput from '@/Shared/SelectInput'
import Checkbox from '@/Shared/Checkbox'
  
export default {
  components: {
    Head,
    Icon,
    Link,
    SearchFilter,
    Pagination,
    FileInput,
    SelectInput,
    Checkbox,
  },
  layout: Layout,
  props: {
    filters: Object,
    projections: Object,
    errors: Object,
    years: Object,
  },
  data() {
    return {
      form: {
        search: this.filters.search,
      },
      overwrite: false,
      formData: {
        projection_file: null,
        financial_year: null,
        units: null,
      },
      openUpload: false,
    }
  },
  computed: {
    user() {
      return this.$inertia.page.props.auth.user
    },
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get('/projections', pickBy(this.form), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    openUploadWindow() {
      this.openUpload = true
    },
    closeModal() {
      this.openUpload = false
      this.$props.errors.projection_file = ''
      this.$props.errors.financial_year = ''
    },
    store() {
      this.formData.overwrite = this.overwrite
      this.$inertia.post('/projections', this.formData, {
        onSuccess: () => {
          this.openUpload = false
        },
      })
    },
  },
}
</script>
    